import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Listing } from 'src/api/v1-api';
import { Flex } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import CompactListingCard from 'src/components/molecules/compact-listing-card';

interface CompactListingCarouselProps {
  listings: Listing[];
  columns: number;
}

const SCROLL_DISTANCE = 280;

const CompactListingCards = memo(function CompactListings({
  listings,
  columns,
}: CompactListingCarouselProps) {
  return (
    <>
      {listings.map((listing) => (
        <CompactListingCard
          key={listing.id}
          listing={listing}
          columns={columns}
          flexProps={{
            flexShrink: 0,
            flexGrow: 0,
            scrollSnapAlign: 'start',
            width: { base: '120px', md: '140px' },
            minWidth: { base: '120px', md: '140px' },
            maxWidth: { base: '120px', md: '140px' },
          }}
        />
      ))}
    </>
  );
});

const CompactListingCarousel = ({ listings, columns }: CompactListingCarouselProps) => {
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (divRef.current) {
      const { scrollLeft, offsetWidth, scrollWidth } = divRef.current;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft + offsetWidth < scrollWidth);
    }
  }, [setShowLeftArrow, setShowRightArrow]);

  useEffect(() => {
    if (divRef.current && listings.length > 0) {
      handleScroll();
    }
  }, [listings]);

  const scroll = (distance: number) => {
    if (divRef.current) {
      const { scrollLeft, scrollWidth, offsetWidth } = divRef.current;
      const maxScrollLeft = scrollWidth - offsetWidth;
      let newScrollLeft = scrollLeft + distance;

      if (newScrollLeft < 0) {
        newScrollLeft = 0;
      } else if (newScrollLeft > maxScrollLeft) {
        newScrollLeft = maxScrollLeft;
      }

      divRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const displayCssProp = {
    base: listings.length >= 2 ? 'block' : 'none',
    md: listings.length >= 2 ? 'block' : 'none',
    lg: listings.length >= columns ? 'block' : 'none',
  };

  return (
    <Flex position="relative" width="100%">
      {showLeftArrow && (
        <ChevronLeftIcon
          position="absolute"
          left={2}
          top="50%"
          transform="translateY(-50%)"
          w={8}
          h={8}
          zIndex="1"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          cursor="pointer"
          onClick={() => scroll(-SCROLL_DISTANCE)}
        />
      )}
      <Flex
        overflowX="auto"
        gap={{ base: '16px', md: '16px' }}
        ref={divRef}
        onScroll={handleScroll}
        scrollSnapType="x mandatory"
        width="100%"
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <CompactListingCards listings={listings} columns={columns} />
      </Flex>
      {showRightArrow && (
        <ChevronRightIcon
          position="absolute"
          right={2}
          top="50%"
          transform="translateY(-50%)"
          w={8}
          h={8}
          zIndex="1"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          display={displayCssProp}
          cursor="pointer"
          onClick={() => scroll(SCROLL_DISTANCE)}
        />
      )}
    </Flex>
  );
};

export default CompactListingCarousel;
